import { any, bool, element, func, string } from "prop-types";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";

import { FieldType } from "@/enums";
import { formFieldPropertyType } from "@/propTypes";
import { useDynamicFormContext } from "./DynamicFormProvider";

import * as S from "./styled";

const DynamicFormItem = ({
  formFieldProperty,
  children,
  hideLabel,
  valuePropName = "value",
  customValidator,
  externalInitialValue,
  getValueProps,
  trigger,
  noStyle,
}) => {
  const intl = useIntl();
  const {
    formConfig,
    formData,
    formRef,
    handleValuesChange,
    getDependentPropertyValue,
    showFormFieldByDependentProperty,
    currentStatus,
    currentProductCategory,
    initialValues: formInitialValues,
  } = useDynamicFormContext();
  const [initialValue, setInitialValue] = useState(externalInitialValue);
  const [isRequiredForStatus, setIsRequiredForStatus] = useState(false);

  /***
   * Whenever a value on the form configuration changes, we need to recalculate
   * and form dependencies we may or may not have.
   */
  useEffect(() => {
    setIsRequiredForStatus(
      formFieldProperty.requiredForStatuses?.find(
        (elm) => elm.toLowerCase() === currentStatus?.toLowerCase()
      )
    );
    if (!currentProductCategory || !formFieldProperty.values) return;

    const defaultValues = formFieldProperty.values.filter((value) =>
      value.DefaultForProductCategories?.includes(currentProductCategory)
    );
    let defaultValue;

    if (defaultValues.length && formFieldProperty.dependsOnPropertyId) {
      const dependentValue = getDependentPropertyValue(
        formFieldProperty.dependsOnPropertyId
      );

      defaultValue = defaultValues?.find(
        (value) =>
          !value.dependsOnPropertyValues?.length ||
          value.dependsOnPropertyValues.includes(dependentValue)
      );
    }

    if (defaultValue) {
      const { name } = formFieldProperty;
      const value = defaultValue.id;

      if (
        !formInitialValues ||
        !Object.prototype.hasOwnProperty.call(formInitialValues, name)
      ) {
        setInitialValue(value);
        if (!formRef.current.getFieldValue(name)) {
          const change = {
            [name]: value,
          };
          formRef.current.setFieldsValue(change);
          handleValuesChange(change);
        }
      }
    }
  }, [
    formConfig,
    formData,
    currentProductCategory,
    formRef,
    getDependentPropertyValue,
    showFormFieldByDependentProperty,
    formFieldProperty.dependsOnPropertyId,
    formFieldProperty.dependsOnPropertyValues,
    formFieldProperty.name,
    formFieldProperty.values,
    formFieldProperty.requiredForStatuses,
    currentStatus,
    handleValuesChange,
    formFieldProperty,
    formInitialValues,
  ]);

  const hasFormInitialValue =
    formInitialValues &&
    Object.prototype.hasOwnProperty.call(
      formInitialValues,
      formFieldProperty.name
    );

  const rules = [
    {
      required:
        formFieldProperty.fieldType !== FieldType.Switch &&
        formFieldProperty.fieldType !== FieldType.Checkbox &&
        formFieldProperty.fieldType !== FieldType.PercentCompleteSlider &&
        isRequiredForStatus,
      message: formFieldProperty.requiredValidationText?.fallback
        ? intl.formatMessage({
            id: formFieldProperty.requiredValidationText.id,
            defaultMessage: formFieldProperty.requiredValidationText.fallback,
          })
        : `${intl.formatMessage({
            id: formFieldProperty.label.id,
            defaultMessage: formFieldProperty.label.fallback,
          })} is required`,
    },
  ];

  if (customValidator) {
    rules.push(customValidator);
  }

  return (
    <S.FormItem
      key={`form-item-${
        Array.isArray(formFieldProperty.name)
          ? formFieldProperty.name.join(".")
          : formFieldProperty.name
      }`}
      name={formFieldProperty.name}
      valuePropName={valuePropName}
      {...(!hasFormInitialValue && initialValue !== undefined
        ? { initialValue }
        : {})}
      noStyle={noStyle}
      getValueProps={getValueProps}
      trigger={trigger}
      label={
        !hideLabel &&
        intl.formatMessage({
          id: formFieldProperty.label.id,
          defaultMessage: formFieldProperty.label.fallback,
        })
      }
      rules={rules}
      labelAlign="left"
      colon="false"
    >
      {children}
    </S.FormItem>
  );
};

DynamicFormItem.propTypes = {
  formFieldProperty: formFieldPropertyType.isRequired,
  children: element,
  hideLabel: bool,
  valuePropName: string,
  customValidator: func,
  externalInitialValue: any,
  getValueProps: func,
  trigger: string,
  noStyle: bool,
};

export { DynamicFormItem };
